import {
  boundedPromise,
  createBoundedSerialQueue,
  DeadlineExceededError,
  notifyDeadlineExceeded,
} from "@-/dev.improve.haniverse.lib.deadline";

export interface BackupProgress {
  completed: number;
  label: string;
  total: number;
}

/**
 * The operation went {@link BACKUP_OPERATION_IDLE_DEADLINE_MS} without progress (or hit the
 * ceiling) and was abandoned. `lastProgress` names the step it was stuck in.
 */
export class BackupOperationExpiredError extends Error {
  override readonly name = "BackupOperationExpiredError";
  constructor(
    readonly lastProgress: string | null,
    readonly idleTimeoutMs: number,
  ) {
    super(
      "This operation timed out. It may still be finishing on this device; try again in a moment.",
    );
  }
}

export interface BackupOperation {
  /** Throws once the operation has expired, so a straggler never resumes its later steps. */
  check: () => void;
  /** When the operation expires unless it reports progress first (absolute ms, moves on `report`). */
  readonly deadline: number;
  /** The label of the most recent progress report, for naming a stalled step. */
  readonly lastProgress: string | null;
  /** Publish progress. Progress restarts the idle deadline; an expired operation throws instead. */
  report: (progress: BackupProgress) => void;
  signal: AbortSignal;
  /** Await `promise`, but never past the operation's expiry. */
  wait: <T>(promise: PromiseLike<T>) => Promise<T>;
}

/**
 * How long an operation may go WITHOUT reporting progress. Every step it awaits is local (a
 * backup page read or write, a native backup job, one sync call that has its own 30s network
 * bound), so a minute of silence means a step that will not answer. Long backups keep running
 * because each saved chunk reports progress and restarts this deadline.
 */
export const BACKUP_OPERATION_IDLE_DEADLINE_MS = 60_000;

/**
 * Hard ceiling for one operation even while it keeps reporting progress. A real archive of the
 * largest decks finishes in well under a minute; only a progress loop that never completes (a
 * pagination bug) reaches this.
 */
export const BACKUP_OPERATION_CEILING_MS = 30 * 60_000;

/**
 * Run `action` with a restart-on-progress deadline. On expiry the awaiter is rejected, `signal`
 * aborts (native jobs cancel, a Web Lock request stops waiting), and `check`/`report`/`wait` throw,
 * so the straggler cannot publish anything after it was abandoned. An in-flight native call cannot
 * be cancelled from here; it is simply no longer awaited.
 *
 * There is deliberately no "wait for settlement" mode any more. Keeping the caller (and with it
 * the shared backup/sync queue) until the work settles is exactly what let one native push that
 * never answered block every later sync and the sign-in database swap forever. The database
 * lifecycle owns its own load gate; the swap holds that gate around the native close/reset/open
 * itself (see `use-actor-sync.ts`), independent of this queue.
 */
export async function runBackupOperation<T>(
  action: (operation: BackupOperation) => Promise<T>,
  onProgress?: (progress: BackupProgress) => void,
  idleTimeoutMs = BACKUP_OPERATION_IDLE_DEADLINE_MS,
): Promise<T> {
  const controller = new AbortController();
  let lastProgress: string | null = null;
  let error: BackupOperationExpiredError | null = null;
  const expiredError = () => (error ??= new BackupOperationExpiredError(lastProgress, idleTimeoutMs));
  let deadline = Date.now() + idleTimeoutMs;
  let done = false;
  let rearm = () => {};
  let settleExpiry = () => {};
  // Settles when the operation expires — `idleTimeoutMs` without progress, or the ceiling — or when
  // the work is done (cleanup). Every wait below races it, so nothing waits past expiry.
  const expiry = boundedPromise(
    {
      deadline: BACKUP_OPERATION_CEILING_MS,
      onDeadline: "resolve",
      operation: "local backup or sync operation (ceiling)",
    },
    (resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      rearm = () => {
        clearTimeout(timer);
        timer = setTimeout(
          () => {
            notifyDeadlineExceeded(
              new DeadlineExceededError(
                "local backup or sync operation (no progress)",
                idleTimeoutMs,
              ),
            );
            resolve();
          },
          Math.max(0, deadline - Date.now()),
        );
      };
      settleExpiry = resolve;
      rearm();
      return () => {
        clearTimeout(timer);
      };
    },
  );
  const expired = expiry.then((): never => {
    if (!done) {
      controller.abort(expiredError());
    }
    throw expiredError();
  });
  // Only observed through `wait`; after completion nobody is left to observe it.
  expired.catch(() => {});

  const check = () => {
    if (controller.signal.aborted || Date.now() >= deadline) {
      throw expiredError();
    }
  };
  const operation: BackupOperation = {
    check,
    get deadline() {
      return deadline;
    },
    get lastProgress() {
      return lastProgress;
    },
    report: (progress) => {
      check();
      lastProgress = progress.label;
      deadline = Date.now() + idleTimeoutMs;
      rearm();
      onProgress?.(progress);
    },
    signal: controller.signal,
    wait: async (promise) => {
      const result = await Promise.race([promise, expired]);
      check();
      return result;
    },
  };
  try {
    return await operation.wait(Promise.resolve().then(() => action(operation)));
  } finally {
    done = true;
    settleExpiry();
  }
}

export type BackupQueue = <T>(action: () => Promise<T>, operation: BackupOperation) => Promise<T>;

/**
 * A FIFO queue of backup/sync coordination jobs. Each job waits in line and runs only until its
 * operation expires; then the line ADVANCES. Advancing is safe here because this queue does not
 * guard a native resource directly: the actor connection serializes pull/push/writes behind its
 * own connection lock, the backup worker serializes its jobs natively, and an abandoned job cannot
 * take another step (`check`/`wait` throw). What the queue orders — recovery-hold checks against
 * sync, backups against restores, the sign-in swap against sync — only needs "no two coordinated
 * steps start together", which the expiry keeps.
 */
export function createBackupQueue(name: string): BackupQueue {
  const queue = createBoundedSerialQueue({
    name,
    onOverrun: "advance",
    // Each job is bounded by its own operation's restart-on-progress deadline (the task below
    // settles at expiry); these only backstop an operation that reports progress forever.
    runDeadline: BACKUP_OPERATION_CEILING_MS,
    waitDeadline: BACKUP_OPERATION_CEILING_MS,
  });
  return async (action, operation) => {
    operation.report({ completed: 0, label: "Waiting for other backup or sync work", total: 1 });
    return operation.wait(
      queue.run(
        "coordinated job",
        async () => {
          operation.check();
          return operation.wait(action());
        },
        // Waiting in line is not progress: give up when the operation would expire anyway.
        { waitDeadline: Math.max(1, operation.deadline - Date.now()) },
      ),
    );
  };
}
